import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './supabase'

export type DiarizedSegment = { speaker: string; text: string; start?: number; end?: number }
export type TranscriptResult = { meetingId: string; status: string; text?: string; segments?: DiarizedSegment[]; summary?: string }

// Cross-view sync event for the Transcribe UI
export const TRANSCRIBE_STATE_EVENT = 'gv:transcribe-state'

export async function uploadAudioToStorage(file: File, prefix = 'uploads'): Promise<{ path: string; publicUrl?: string }> {
    const ext = (file.name.split('.').pop() || 'wav').toLowerCase()
    const fileName = `${prefix}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
    const { error } = await supabase.storage.from('audio').upload(fileName, file, { upsert: false })
    if (error) throw new Error(`Failed to upload audio: ${error.message}`)
    const { data } = supabase.storage.from('audio').getPublicUrl(fileName)
    return { path: fileName, publicUrl: data?.publicUrl }
}

// Start an upload that continues regardless of component lifecycle. It broadcasts updates
// via window events and mirrors minimal state into sessionStorage so a new view can resume.
export async function startBackgroundUpload(file: File, prefix = 'uploads'): Promise<{ path: string; publicUrl?: string }> {
    try {
        // Persist intent immediately
        try {
            sessionStorage.setItem('transcribe:status', 'uploading')
            sessionStorage.setItem('transcribe:fileName', file.name)
        } catch { /* ignore */ }
        // Broadcast that an upload has started
        try {
            window.dispatchEvent(new CustomEvent(TRANSCRIBE_STATE_EVENT, { detail: { status: 'uploading', uploading: true, fileName: file.name } }))
        } catch { /* ignore */ }
        const res = await uploadAudioToStorage(file, prefix)
        // Persist completion
        try {
            sessionStorage.setItem('transcribe:status', 'idle')
            sessionStorage.setItem('transcribe:uploadedPath', res.path)
            sessionStorage.setItem('transcribe:fileName', file.name)
        } catch { /* ignore */ }
        // Broadcast completion so any mounted view can enable Start
        try {
            window.dispatchEvent(new CustomEvent(TRANSCRIBE_STATE_EVENT, { detail: { status: 'idle', uploading: false, uploadedPath: res.path, fileName: file.name } }))
        } catch { /* ignore */ }
        return res
    } catch (e: any) {
        // Persist error and broadcast
        const message = e?.message || 'Failed to upload file'
        try {
            sessionStorage.setItem('transcribe:status', 'error')
            sessionStorage.setItem('transcribe:error', message)
        } catch { /* ignore */ }
        try {
            window.dispatchEvent(new CustomEvent(TRANSCRIBE_STATE_EVENT, { detail: { status: 'error', uploading: false, error: message } }))
        } catch { /* ignore */ }
        throw e
    }
}

export async function firefliesUploadFromStorage(opts: { storagePath: string; title?: string | null; language?: string | null; attendees?: Array<{ name?: string | null; email?: string | null }> }): Promise<{ meeting_id: string; status: string }> {
    const started = Date.now()
    const payload = { action: 'upload', storagePath: opts.storagePath, title: opts.title || null, language: opts.language || null, attendees: opts.attendees || [] }
    console.debug('[transcribe] invoking fireflies-transcribe upload', { payload })
    const { data, error } = await supabase.functions.invoke('fireflies-transcribe', { body: payload })
    const elapsed = Date.now() - started
    if (error) {
        console.error('[transcribe] upload invoke error', { error, elapsed, context: 'Check Supabase Edge logs and FIREFLIES_API_KEY' })
        // Try a raw fetch to capture the full JSON body of the error response without losing details.
        let composedMessage: string | null = null
        try {
            const raw = await rawInvoke(payload)
            console.error('[transcribe] raw upload error body', raw)
            if (raw.body && typeof raw.body === 'object') {
                const errorMsg = (raw.body as any).error || (raw.body as any).message || 'Fireflies upload failed'
                const errorDetails = (raw.body as any).errors || (raw.body as any).hint || null
                composedMessage = `${errorMsg}${errorDetails ? ` (${typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails)})` : ''}`
            } else {
                composedMessage = `Edge upload failed: ${JSON.stringify(raw)}`
            }
        } catch (rawError: any) {
            // If raw fetch also fails, provide a clearer error message
            const errorMessage = (error as any)?.message || rawError?.message || 'Unknown error'
            composedMessage = `Edge Function error: ${errorMessage}`
        }
        throw new Error(composedMessage || 'Edge Function error: Unknown')
    }
    console.debug('[transcribe] upload invoke response', { data, elapsed })
    if (!data?.ok) throw new Error(data?.error || 'Fireflies upload failed')
    return data.data
}

export async function firefliesGetStatus(meetingId: string): Promise<TranscriptResult> {
    const started = Date.now()
    console.debug('[transcribe] invoking fireflies-transcribe status', { meetingId })
    const { data, error } = await supabase.functions.invoke('fireflies-transcribe', { body: { action: 'status', meetingId } })
    const elapsed = Date.now() - started
    if (error) {
        console.error('[transcribe] status invoke error', { error, elapsed })
        try {
            const raw = await rawInvoke({ action: 'status', meetingId })
            console.error('[transcribe] raw status error body', raw)
            throw new Error(`Edge status failed: ${JSON.stringify(raw)}`)
        } catch (e: any) {
            const details = (error as any)?.context || (error as any)?.message || String(error)
            throw new Error(`Edge status failed (no raw body): ${details}`)
        }
    }
    console.debug('[transcribe] status invoke response', { data, elapsed })
    if (!data?.ok) throw new Error(data?.error || 'Fireflies status failed')
    const m = data.data
    return {
        meetingId: m?.id || meetingId,
        status: m?.status || 'unknown',
        text: m?.transcript?.text || '',
        summary: m?.summary || '',
        segments: (m?.transcript?.segments || []).map((s: any) => ({ speaker: s?.speaker || 'Speaker', text: s?.text || '', start: s?.start, end: s?.end }))
    }
}

// Optional: quick probe to assess CORS/preflight issues from the client
export async function probeEdgeFunction(): Promise<{ ok: boolean; details: any }> {
    try {
        const url = `${SUPABASE_URL}/functions/v1/fireflies-transcribe`
        const res = await fetch(url, { method: 'OPTIONS' })
        const headers: Record<string, string> = {}
        res.headers.forEach((v, k) => { headers[k] = v })
        return { ok: res.ok, details: { status: res.status, headers } }
    } catch (e: any) {
        return { ok: false, details: { error: e?.message || String(e) } }
    }
}

async function rawInvoke(body: Record<string, unknown>) {
    const url = `${SUPABASE_URL}/functions/v1/fireflies-transcribe`
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(body),
    })
    const text = await res.text()
    let json: any
    try { json = JSON.parse(text) } catch { json = { raw: text } }
    return { ok: res.ok, status: res.status, body: json }
}

// Compatibility stubs for functions referenced by legacy Assistant code.
// These intentionally fail fast in development to surface incorrect usage paths.
export async function waitForTranscriptId(_clientRefId: string): Promise<string> {
    throw new Error('waitForTranscriptId is not implemented in current transcribe lib')
}

export async function listRecentTranscripts(_userId?: string | null): Promise<any[]> {
    // Return empty to indicate no resumable sessions by default
    return []
}

export async function getCachedTranscript(_userId: string | undefined, _id: string): Promise<any> {
    // No cache available in this trimmed implementation
    return null
}

export async function deleteRecentTranscript(_userId: string, _idOrRef: string): Promise<void> {
    throw new Error('deleteRecentTranscript is not implemented in current transcribe lib')
}

export async function pingFireflies(): Promise<any> {
    throw new Error('pingFireflies is not implemented in current transcribe lib')
}

export async function testWebhook(_clientRefId?: string, _transcriptId?: string): Promise<any> {
    throw new Error('testWebhook is not implemented in current transcribe lib')
}

export async function diagStatusRaw(_id: string): Promise<any> {
    throw new Error('diagStatusRaw is not implemented in current transcribe lib')
}

export async function diagStatusWide(_id: string): Promise<any> {
    throw new Error('diagStatusWide is not implemented in current transcribe lib')
}

export async function forceResolveClientRef(_clientRefId: string): Promise<any> {
    throw new Error('forceResolveClientRef is not implemented in current transcribe lib')
}